/**
 * KEJJA — Home theme settings.
 * Pick a preset or customize primary/accent colors.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, Check, Sparkles, Loader2 } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useHomeTheme, type HomeTheme } from '@/contexts/HomeThemeContext';
import { cn } from '@/lib/utils';

interface Preset {
  key: string; name: string; emoji: string; description: string;
  primary: string; accent: string; warm: boolean;
}

export default function ThemePage() {
  const { data: homesData } = useMyHomes();
  const home = homesData?.homes?.[0]?.home;
  const homeId = home?.id;
  const qc = useQueryClient();
  const { theme: liveTheme, setTheme: setLive } = useHomeTheme();

  const [primary, setPrimary] = useState(liveTheme.primary ?? '#196287');
  const [accent, setAccent] = useState(liveTheme.accent ?? '#0ea5e9');
  const [warm, setWarm] = useState(liveTheme.warm ?? false);
  const [selectedPreset, setSelectedPreset] = useState(liveTheme.preset ?? 'classic');

  useEffect(() => {
    setPrimary(liveTheme.primary ?? '#196287');
    setAccent(liveTheme.accent ?? '#0ea5e9');
    setWarm(liveTheme.warm ?? false);
    setSelectedPreset(liveTheme.preset ?? 'classic');
  }, [liveTheme.primary, liveTheme.accent, liveTheme.warm, liveTheme.preset]);

  const presetsQ = useQuery({
    queryKey: ['theme-presets', homeId],
    queryFn: () => api.get<{ presets: Preset[] }>(`/homes/${homeId}/theme/presets`),
    enabled: !!homeId,
  });

  const saveMut = useMutation({
    mutationFn: (t: HomeTheme) => api.put<{ theme: HomeTheme }>(`/homes/${homeId}/theme`, t),
    onSuccess: (data) => {
      setLive(data.theme);
      qc.invalidateQueries({ queryKey: ['home-theme', homeId] });
      toast.success('Theme saved');
    },
  });

  function applyPreset(p: Preset) {
    setSelectedPreset(p.key);
    setPrimary(p.primary);
    setAccent(p.accent);
    setWarm(p.warm);
    setLive({ preset: p.key, primary: p.primary, accent: p.accent, warm: p.warm });
  }

  function applyCustom() {
    setLive({ preset: 'custom', primary, accent, warm });
    setSelectedPreset('custom');
  }

  function save() {
    saveMut.mutate({ preset: selectedPreset, primary, accent, warm });
  }

  if (!homeId) return null;

  return (
    <div className="space-y-6 pb-20 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Palette className="w-6 h-6" style={{ color: 'var(--home-primary)' }} /> Home theme
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Make {home?.name || 'your home'} feel like yours. Pick a mood or set custom colors.
        </p>
      </div>

      {/* Presets */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="w-4 h-4 text-amber-500" /> Mood presets
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(presetsQ.data?.presets ?? []).map(p => {
              const active = selectedPreset === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p)}
                  className={cn(
                    'p-4 rounded-xl border-2 text-left transition-all hover:shadow-md',
                    active ? 'shadow-md ring-2 ring-offset-2 ring-offset-background' : 'border-border',
                  )}
                  style={active ? { borderColor: p.primary, ['--tw-ring-color' as any]: p.primary } : undefined}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-2xl">{p.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground">{p.description}</div>
                    </div>
                    {active && (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white"
                           style={{ background: p.primary }}>
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <div className="h-3 flex-1 rounded-full" style={{ background: p.primary }} />
                    <div className="h-3 flex-1 rounded-full" style={{ background: p.accent }} />
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Custom colors */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Palette className="w-4 h-4" /> Custom colors
          </div>
          <p className="text-xs text-muted-foreground">
            Fine-tune the primary and accent colors. These flow through buttons, links, highlights, and other accents.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase font-mono text-muted-foreground">Primary</label>
              <div className="flex gap-2 mt-1">
                <input type="color" value={primary} onChange={e => setPrimary(e.target.value)}
                       className="w-12 h-10 rounded cursor-pointer" />
                <Input value={primary} onChange={e => setPrimary(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-mono text-muted-foreground">Accent</label>
              <div className="flex gap-2 mt-1">
                <input type="color" value={accent} onChange={e => setAccent(e.target.value)}
                       className="w-12 h-10 rounded cursor-pointer" />
                <Input value={accent} onChange={e => setAccent(e.target.value)} />
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={warm} onChange={e => setWarm(e.target.checked)} />
            <span className="text-sm">Warm background overlay (subtle parchment tone)</span>
          </label>
          <Button variant="outline" onClick={applyCustom}>Preview custom</Button>
        </CardContent>
      </Card>

      {/* Live preview */}
      <Card style={{ borderColor: primary + '40' }}>
        <CardContent className="p-5">
          <div className="text-xs uppercase font-mono text-muted-foreground mb-3">Live preview</div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button style={{ background: primary, color: 'white' }}>Primary button</Button>
            <Button variant="outline" style={{ borderColor: accent, color: accent }}>Accent outline</Button>
            <span style={{ color: primary }} className="font-semibold">Sample link</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium text-white"
                  style={{ background: accent }}>Badge</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saveMut.isPending}
                style={{ background: primary, color: 'white' }}>
          {saveMut.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save theme for everyone in this home
        </Button>
      </div>
    </div>
  );
}
